/**
 * Internal definitions of project views.
 * Each entry defines the internal name, numeric ID, and translation key.
 * @private
 * @readonly
 */
const _ProjectViewDefinitions = Object.freeze([
  { value: 1, name: 'Dashboard', translationKey: 'project.tabs.dashboard' },
  { value: 2, name: 'Files', translationKey: 'project.tabs.files' },
  { value: 3, name: 'Kanban', translationKey: 'project.tabs.kanban' },
  { value: 4, name: 'Reports', translationKey: 'project.tabs.reports' },
  { value: 5, name: 'Tasks', translationKey: 'project.tabs.tasks' },
  { value: 0, name: 'Unknown', translationKey: undefined },
]);

export const getProjectViewDefinitions = () => {
  return _ProjectViewDefinitions.filter(n => n.value !== 0);
};

/**
 * An enumeration of valid project view types.
 * @enum {number}
 */
export const ProjectViewType = Object.freeze(
  _ProjectViewDefinitions.reduce((acc, def) => {
    acc[def.name] = def.value;
    return acc;
  }, {})
);

/**
 * Internal mapping of project view types to UI/localization labels.
 * @private
 * @readonly
 * @type {Object<number, string|undefined>}
 */
const _TranslationKeyForType = Object.freeze(
  _ProjectViewDefinitions.reduce((acc, def) => {
    acc[def.value] = def.translationKey;
    return acc;
  }, {})
);

/**
 * Fallback translation key.
 * @private
 */
const _FallbackTranslationKey = "project.tabs.unknown";

/**
 * Returns an ID string for a project tab element.
 * @param {number|string} projectViewId A project view identifier.
 * @returns {string}
 */
export const getAriaTabId = (projectViewId) => `project-tab-${projectViewId}`;

/**
 * Returns an ID string for a project tab panel.
 * @param {ProjectViewType} projectViewType An enumeration value for a project view type.
 * @returns {string}
 */
export const getAriaTabPanelId = (projectViewType) => `project-tabpanel-${projectViewType}`;

/**
 * Returns a UI/localization translation key for the given project view type.
 * If the view type is unknown or unsupported, a default fallback key can be returned.
 *
 * @param {ProjectViewType} projectViewType A value from the ProjectViewType enum.
 * @param {boolean} [useFallback=false] Determines whether the default fallback key should be used or not.
 * @returns {string|undefined} A translation key for localization.
 */
export const getTranslationKey = (projectViewType, useFallback = false) => {
  if (projectViewType in _TranslationKeyForType) {
    const key = _TranslationKeyForType[projectViewType];
    if (key !== undefined) return key;
  }
  return useFallback ? _FallbackTranslationKey : undefined;
};

/**
 * Returns default project tabs data.
 * Useful as fallback when a project has no defined views.
 *
 * @returns {Array<Object>} An array of tab data objects.
 */
export const getDefaultProjectTabsData = () => {
  return [{
    viewId: -1, // Negative viewId indicates an added view.
    jsonViewSettings: "{}",
    type: ProjectViewType.Tasks,
    ariaControls: getAriaTabPanelId(ProjectViewType.Tasks),
  }];
};

/**
 * Returns a list of project views data for a given project.
 * If no views are found, data for the default view (Tasks) is returned.
 *
 * @param {Object} project A project object that may contain a `projectViews` array.
 * @returns {Array<Object>} An array of tab data objects with added ariaControls.
 */
export const getProjectTabsData = (project) => {
  const projectViews = project?.projectViews;
  const haveViews = Array.isArray(projectViews) && projectViews.length > 0;

  return (haveViews ? projectViews : getDefaultProjectTabsData()).map(tab => ({
    ...tab,
    ariaControls: getAriaTabPanelId(tab.type),
  }));
};

/**
 * Central utility object for project view-related logic.
 */
const ProjectUtils = {
  getAriaTabId,
  getAriaTabPanelId,
  getDefaultProjectTabsData,
  getProjectTabsData,
  getProjectViewDefinitions,
  getTranslationKey,
  ProjectViewType,
};

export default ProjectUtils;
